import React, { useState, useEffect } from 'react';
import { useModIQProject } from '../hooks/useModIQProject';

const ProjectManagementPanel = ({ 
  currentModIQState = null,
  onProjectLoaded = null,
  onProjectNameUpdate = null,
  onProjectIdUpdate = null,
  projectAddress = null,
  initialProjectId = null // Project ID from URL to auto-load
}) => {
  const { 
    currentProjectId,
    projectName, 
    projects, 
    saveProject,
    loadProject, 
    createNewProject,
    deleteProject,
    isSaving,
    isLoading,
    lastSaved,
    saveError
  } = useModIQProject();
  
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectModalMode, setProjectModalMode] = useState('create'); // 'create', 'load', or 'save'
  const [newProjectName, setNewProjectName] = useState('');
  const [selectedForDelete, setSelectedForDelete] = useState(new Set()); // Track projects selected for bulk delete
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState(''); // Search filter for projects
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false); // Track if we've auto-loaded from URL
  
  // Auto-load project from URL parameter
  useEffect(() => {
    const autoLoadProject = async () => {
      if (initialProjectId && !hasAutoLoaded && !currentProjectId) {
        console.log('🔗 Auto-loading project from URL:', initialProjectId);
        setHasAutoLoaded(true);
        
        const result = await loadProject(initialProjectId);
        if (result.success && result.data) {
          console.log('✅ Project auto-loaded from URL:', result.data.projectName);
          if (onProjectLoaded) {
            onProjectLoaded(result.data, initialProjectId);
          }
          if (onProjectIdUpdate) {
            onProjectIdUpdate(initialProjectId);
          }
          if (onProjectNameUpdate && result.data.projectName) {
            onProjectNameUpdate(result.data.projectName);
          }
        } else {
          console.warn('⚠️ Failed to auto-load project from URL:', initialProjectId, result.error);
        }
      }
    };
    
    autoLoadProject();
  }, [initialProjectId, hasAutoLoaded, currentProjectId, loadProject, onProjectLoaded, onProjectIdUpdate, onProjectNameUpdate]);
  
  // Filter projects based on search query
  const filteredProjects = projects.filter(project => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const name = (project.projectName || '').toLowerCase();
    return name.includes(query);
  });
  
  // Helper to format date nicely
  const formatDate = (dateValue) => {
    if (!dateValue) return 'Unknown date';
    
    try {
      // Handle Firebase Timestamp (has toDate method)
      if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        const date = dateValue.toDate();
        return formatDateString(date);
      }
      // Handle seconds/nanoseconds object (Firebase timestamp format)
      if (dateValue.seconds) {
        const date = new Date(dateValue.seconds * 1000);
        return formatDateString(date);
      }
      // Handle regular JavaScript Date or ISO string
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return formatDateString(date);
      }
      return 'Unknown date';
    } catch (error) {
      console.warn('Error parsing date:', error);
      return 'Unknown date';
    }
  };
  
  // Format date as readable string with time
  const formatDateString = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      // Today - show time
      return `Today at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  // Notify wrapper when project name changes
  useEffect(() => {
    if (onProjectNameUpdate && projectName) {
      onProjectNameUpdate(projectName);
    }
  }, [projectName, onProjectNameUpdate]);

  // Notify wrapper when project ID changes
  useEffect(() => {
    if (onProjectIdUpdate) {
      onProjectIdUpdate(currentProjectId);
    }
  }, [currentProjectId, onProjectIdUpdate]);

  // Debug: Monitor currentProjectId changes
  useEffect(() => {
    console.log('🔍 ProjectManagementPanel currentProjectId changed:', currentProjectId);
  }, [currentProjectId]);

  // Debug: Monitor projectName changes  
  useEffect(() => {
    console.log('🔍 ProjectManagementPanel projectName changed:', projectName);
  }, [projectName]);

  const handleCreateProject = async () => {
    if (newProjectName.trim()) {
      // Create a basic project structure for saving
      const basicProjectData = {
        projectName: newProjectName.trim(),
        // Add minimal required data structure
        parcelCoordinates: null,
        parcelWidth: 200,
        parcelLength: 150,
        buildPaths: [],
        boxes: [],
        layoutState: {
          buildPaths: [],
          boxes: [],
          selectedOptimizations: {},
          buildingOptimizations: {},
          selectedBuildingId: null
        }
      };
      
      // Save the project to storage (Firebase or localStorage)
      const result = await saveProject(basicProjectData, null, newProjectName.trim());
      
      if (result.success) {
        setNewProjectName('');
        setShowProjectModal(false);
        console.log(`✅ New project "${newProjectName.trim()}" created successfully!`);
      } else {
        console.error('Failed to create project:', result.error);
        alert('Failed to create project: ' + result.error);
      }
    }
  };

  const handleSaveCurrentProject = async () => {
    if (currentModIQState && newProjectName.trim()) {
      const result = await saveProject(currentModIQState, currentProjectId, newProjectName.trim());
      
      if (result.success) {
        setNewProjectName('');
        setShowProjectModal(false);
        console.log(`✅ Project "${newProjectName.trim()}" saved successfully!`);
      } else {
        console.error('Failed to save project:', result.error);
        alert('Failed to save project: ' + result.error);
      }
    } else if (!currentModIQState) {
      alert('No current project state available to save.');
    }
  };

  // Silent save for existing projects
  const handleSilentSave = async () => {
    console.log('🔍 Silent save debug:', {
      currentModIQState: !!currentModIQState,
      currentProjectId: currentProjectId,
      projectName: projectName
    });
    
    if (currentModIQState) {
      const result = await saveProject(currentModIQState, currentProjectId, projectName || 'Untitled Project');
      
      if (result.success) {
        console.log(`✅ Project "${projectName || 'Untitled Project'}" saved silently!`);
      } else {
        console.error('Failed to save project:', result.error);
        alert('Failed to save project: ' + result.error);
      }
    } else {
      alert('No current project state available to save.');
    }
  };

  const handleLoadProject = async (projectId) => {
    const result = await loadProject(projectId);
    if (result.success) {
      setShowProjectModal(false);
      
      // Call the callback to restore the project data to the main application
      if (onProjectLoaded) {
        onProjectLoaded(result.data, projectId); // Pass both data and ID
      } else {
        console.warn('No onProjectLoaded callback provided - project data not applied to application');
        alert('Project loaded but cannot be applied to the application. Please check the integration.');
      }
    } else {
      console.error('Failed to load project:', result.error);
      alert('Failed to load project: ' + result.error);
    }
  };

  const handleDeleteProject = async (projectId, projectName) => {
    if (window.confirm(`Are you sure you want to delete "${projectName}"? This action cannot be undone.`)) {
      const result = await deleteProject(projectId);
      
      if (result.success) {
        console.log(`✅ Project "${projectName}" deleted successfully!`);
      } else {
        console.error('Failed to delete project:', result.error);
        alert('Failed to delete project: ' + result.error);
      }
    }
  };

  // Toggle project selection for bulk delete
  const toggleProjectSelection = (projectId) => {
    setSelectedForDelete(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  // Select/deselect all filtered projects
  const toggleSelectAll = () => {
    const filteredIds = filteredProjects.map(p => p.projectId);
    const allFilteredSelected = filteredIds.every(id => selectedForDelete.has(id));
    
    if (allFilteredSelected && filteredIds.length > 0) {
      // Deselect all filtered
      setSelectedForDelete(prev => {
        const newSet = new Set(prev);
        filteredIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // Select all filtered
      setSelectedForDelete(prev => {
        const newSet = new Set(prev);
        filteredIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  };

  // Bulk delete selected projects
  const handleBulkDelete = async () => {
    if (selectedForDelete.size === 0) return;
    
    const count = selectedForDelete.size;
    if (!window.confirm(`Are you sure you want to delete ${count} project${count > 1 ? 's' : ''}? This action cannot be undone.`)) {
      return;
    }
    
    setIsBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const projectId of selectedForDelete) {
      const result = await deleteProject(projectId);
      if (result.success) {
        successCount++;
      } else {
        failCount++;
        console.error(`Failed to delete project ${projectId}:`, result.error);
      }
    }
    
    setIsBulkDeleting(false);
    setSelectedForDelete(new Set());
    
    if (failCount === 0) {
      console.log(`✅ Successfully deleted ${successCount} project${successCount > 1 ? 's' : ''}!`);
    } else {
      alert(`Deleted ${successCount} project${successCount > 1 ? 's' : ''}, but ${failCount} failed.`);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (projectModalMode === 'create') {
        handleCreateProject();
      } else if (projectModalMode === 'save') {
        handleSaveCurrentProject();
      }
    }
  };

  // Get current project info
  const currentProject = {
    id: currentProjectId,
    name: projectName || 'Untitled Project'
  };

  return (
    <>
      {/* Project Name Panel */}
      <div style={{
        backgroundColor: 'white',
        padding: '12px',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        marginBottom: '12px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h4 style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#374151',
              margin: '0 0 4px 0'
            }}>
              Project: {currentProject.name}
            </h4>
            {projectAddress && (
              <div style={{
                fontSize: '0.75rem',
                color: '#6b7280',
                marginBottom: '4px',
                maxWidth: '300px', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap',
                textTransform: 'capitalize',
                textAlign: 'left'
              }}>
                {projectAddress.toLowerCase()}
              </div>
            )}
            <div style={{
              fontSize: '0.75rem',
              color: '#6b7280',
              textAlign: 'left'
            }}>
              {lastSaved ? `Last saved: ${lastSaved.toLocaleString()}` : 'Not saved yet'}
              {isSaving && ' • Saving...'}
              {saveError && ` • Error: ${saveError}`}
              {!isSaving && !saveError && currentModIQState && ' • Auto-save enabled'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            {/* Save Current Work Button */}
            {currentModIQState && (
              <button
                onClick={() => {
                  console.log('💾 Save button clicked:', {
                    currentProjectId: currentProjectId,
                    projectName: projectName,
                    hasBoth: !!(currentProjectId && projectName)
                  });
                  
                  // If we have a current project ID, save silently
                  if (currentProjectId && projectName) {
                    console.log('📝 Using silent save for existing project');
                    handleSilentSave();
                  } else {
                    console.log('📝 Showing save dialog for new project');
                    // No current project, show save dialog
                    setProjectModalMode('save');
                    setNewProjectName(projectName || '');
                    setShowProjectModal(true);
                  }
                }}
                style={{
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '0.375rem',
                  backgroundColor: '#10b981',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}
                title={currentProjectId && projectName ? "Save Changes" : "Save Current Work"}
                disabled={isSaving}
              >
                {isSaving ? '...' : 'Save'}
              </button>
            )}
            
            {/* Project Management Button */}
            <button
              onClick={() => {
                setProjectModalMode('create');
                setShowProjectModal(true);
              }}
              style={{
                padding: '6px',
                border: 'none',
                borderRadius: '0.375rem',
                backgroundColor: '#f3f4f6',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Project Management"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Project Management Modal */}
      {showProjectModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                Project Management
              </h3>
              <button
                onClick={() => setShowProjectModal(false)}
                style={{
                  border: 'none',
                  background: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '0',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            {/* Mode Toggle */}
            <div style={{
              display: 'flex',
              marginBottom: '20px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <button
                onClick={() => setProjectModalMode('save')}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  backgroundColor: projectModalMode === 'save' ? '#10b981' : '#f9fafb',
                  color: projectModalMode === 'save' ? 'white' : '#374151',
                  cursor: currentModIQState ? 'pointer' : 'not-allowed',
                  fontWeight: '500',
                  opacity: currentModIQState ? 1 : 0.5
                }}
                disabled={!currentModIQState}
              >
                Save Current Work
              </button>
              <button
                onClick={() => setProjectModalMode('create')}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  backgroundColor: projectModalMode === 'create' ? '#3b82f6' : '#f9fafb',
                  color: projectModalMode === 'create' ? 'white' : '#374151',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Create New Project
              </button>
              <button
                onClick={() => setProjectModalMode('load')}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  backgroundColor: projectModalMode === 'load' ? '#3b82f6' : '#f9fafb',
                  color: projectModalMode === 'load' ? 'white' : '#374151',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Load Project
              </button>
            </div>

            {/* Save Current Work Mode */}
            {projectModalMode === 'save' && (
              <div>
                <div style={{
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  marginBottom: '12px'
                }}>
                  Save your current work to {currentProjectId ? 'update the existing project or create a new one' : 'a new project'}:
                </div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Project Name
                </label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter project name..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    marginBottom: '16px'
                  }}
                  autoFocus
                />
                <button
                  onClick={handleSaveCurrentProject}
                  disabled={!newProjectName.trim() || isSaving}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: (newProjectName.trim() && !isSaving) ? '#10b981' : '#d1d5db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: (newProjectName.trim() && !isSaving) ? 'pointer' : 'not-allowed'
                  }}
                >
                  {isSaving ? 'Saving...' : 'Save Current Work'}
                </button>
              </div>
            )}

            {/* Create Mode */}
            {projectModalMode === 'create' && (
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Project Name
                </label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter project name..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    marginBottom: '16px'
                  }}
                  autoFocus
                />
                <button
                  onClick={handleCreateProject}
                  disabled={!newProjectName.trim()}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: newProjectName.trim() ? '#3b82f6' : '#d1d5db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: newProjectName.trim() ? 'pointer' : 'not-allowed'
                  }}
                >
                  Create & Save Project
                </button>
              </div>
            )}

            {/* Load Mode */}
            {projectModalMode === 'load' && (
              <div>
                {/* Search Box */}
                <div style={{ marginBottom: '12px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Search projects..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>
                
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#6b7280'
                  }}>
                    {searchQuery ? `${filteredProjects.length} of ${projects.length} projects` : `${projects.length} projects`}
                  </div>
                  {filteredProjects.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={toggleSelectAll}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          color: '#374151',
                          cursor: 'pointer',
                          fontSize: '0.7rem'
                        }}
                      >
                        {selectedForDelete.size === filteredProjects.length && filteredProjects.length > 0 ? 'Deselect All' : 'Select All'}
                      </button>
                      {selectedForDelete.size > 0 && (
                        <button
                          onClick={handleBulkDelete}
                          disabled={isBulkDeleting}
                          style={{
                            padding: '4px 8px',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            cursor: isBulkDeleting ? 'wait' : 'pointer',
                            fontSize: '0.7rem',
                            fontWeight: '500',
                            opacity: isBulkDeleting ? 0.7 : 1
                          }}
                        >
                          {isBulkDeleting ? 'Deleting...' : `Delete ${selectedForDelete.size} Selected`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {isLoading ? (
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#6b7280'
                  }}>
                    Loading projects...
                  </div>
                ) : projects.length === 0 ? (
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontStyle: 'italic'
                  }}>
                    No saved projects found
                  </div>
                ) : filteredProjects.length === 0 ? (
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontStyle: 'italic'
                  }}>
                    No projects match "{searchQuery}"
                  </div>
                ) : (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {filteredProjects.map((project) => (
                      <div
                        key={project.projectId}
                        style={{
                          padding: '12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          marginBottom: '8px',
                          backgroundColor: selectedForDelete.has(project.projectId) 
                            ? '#fef2f2' 
                            : currentProjectId === project.projectId 
                              ? '#eff6ff' 
                              : 'white',
                          borderColor: selectedForDelete.has(project.projectId)
                            ? '#fca5a5'
                            : currentProjectId === project.projectId 
                              ? '#3b82f6' 
                              : '#e5e7eb'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          {/* Checkbox for bulk selection */}
                          <input
                            type="checkbox"
                            checked={selectedForDelete.has(project.projectId)}
                            onChange={() => toggleProjectSelection(project.projectId)}
                            style={{
                              width: '16px',
                              height: '16px',
                              marginRight: '10px',
                              cursor: 'pointer',
                              accentColor: '#ef4444'
                            }}
                            title="Select for bulk delete"
                          />
                          <div 
                            onClick={() => handleLoadProject(project.projectId)}
                            style={{
                              flex: 1,
                              cursor: 'pointer'
                            }}
                          >
                            <div style={{ fontWeight: '500', color: '#374151' }}>
                              {project.projectName || 'Untitled Project'}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                              Last saved: {formatDate(project.updatedAt || project.createdAt)}
                            </div>
                          </div>
                          
                          {/* Delete Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent triggering the load action
                              handleDeleteProject(project.projectId, project.projectName || 'Untitled Project');
                            }}
                            style={{
                              padding: '4px 8px',
                              border: 'none',
                              borderRadius: '4px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              marginLeft: '8px'
                            }}
                            title="Delete Project"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectManagementPanel; 